import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';

const initialFormState = {
  fullName: '',
  email: '',
  company: '',
  phone: '',
  topic: 'general',
  message: ''
};

const ContactPage = () => {
  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const validate = () => {
    const nextErrors = {};
    if (!formData.fullName.trim()) {nextErrors.fullName = 'Full name is required.';}
    if (!formData.email.trim()) {
      nextErrors.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      nextErrors.email = 'Enter a valid email address.';
    }
    if (!formData.message.trim()) {nextErrors.message = 'Please include a brief message.';}
    return nextErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error('Please complete the required fields.');
      return;
    }

    const topicLabels = {
      general: 'General Inquiry',
      sales: 'Sales',
      partnership: 'Partnership',
      media: 'Media / Press',
      hiring: 'Hiring / Careers'
    };

    const subject = `Alfred Voice Contact – ${topicLabels[formData.topic]}`;
    const bodyLines = [
      `Name: ${formData.fullName}`,
      `Email: ${formData.email}`,
      `Company: ${formData.company || 'N/A'}`,
      `Phone: ${formData.phone || 'N/A'}`,
      `Topic: ${topicLabels[formData.topic]}`,
      '',
      'Message:',
      formData.message
    ];

    const mailtoLink = `mailto:hello@relaywork.co?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyLines.join('\n'))}`;

    toast.success('Opening your email client to finish the message...');
    window.location.href = mailtoLink;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-white py-16">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">Get in Touch with Alfred Voice</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Share how we can help and our team will reach out within one business day. For support-related issues please
            visit the <Link to="/support" className="text-indigo-600 font-semibold hover:text-indigo-500">support desk</Link> instead.
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="fullName" className="block text-sm font-semibold text-gray-700 mb-2">
                  Full name <span className="text-rose-500">*</span>
                </label>
                <input
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="Jordan Winters"
                  className={`w-full rounded-xl border px-4 py-3 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 ${errors.fullName ? 'border-rose-400 focus:ring-rose-400' : 'border-gray-200'}`}
                />
                {errors.fullName && <p className="mt-1 text-sm text-rose-500">{errors.fullName}</p>}
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Work email <span className="text-rose-500">*</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@company.com"
                  className={`w-full rounded-xl border px-4 py-3 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 ${errors.email ? 'border-rose-400 focus:ring-rose-400' : 'border-gray-200'}`}
                />
                {errors.email && <p className="mt-1 text-sm text-rose-500">{errors.email}</p>}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                  Company (optional)
                </label>
                <input
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Relay Work"
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone (optional)
                </label>
                <input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+1 (555) 123-4567"
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="topic" className="block text-sm font-semibold text-gray-700 mb-2">
                  Topic
                </label>
                <select
                  id="topic"
                  name="topic"
                  value={formData.topic}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                >
                  <option value="general">General inquiry</option>
                  <option value="sales">Sales / plan information</option>
                  <option value="partnership">Partnership opportunity</option>
                  <option value="media">Media / press</option>
                  <option value="hiring">Hiring / careers</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                Message <span className="text-rose-500">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                rows={6}
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us what you would like to accomplish with Alfred Voice and how we can help."
                className={`w-full rounded-xl border px-4 py-3 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 ${errors.message ? 'border-rose-400 focus:ring-rose-400' : 'border-gray-200'}`}
              />
              {errors.message && <p className="mt-1 text-sm text-rose-500">{errors.message}</p>}
            </div>

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <p className="text-sm text-gray-500">
                We reply from <span className="font-semibold text-gray-700">hello@relaywork.co</span>. Submitting opens your email client with all details pre-filled.
              </p>
              <button
                type="submit"
                className="inline-flex items-center bg-gray-900 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:bg-gray-800 hover:-translate-y-0.5 transition-all"
              >
                Send message
              </button>
            </div>
          </form>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-6 text-sm text-gray-600">
          <div className="bg-white rounded-2xl shadow border border-gray-100 p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-2">Office hours</h2>
            <p>Monday – Friday<br />9:00 AM – 6:00 PM ET</p>
          </div>
          <div className="bg-white rounded-2xl shadow border border-gray-100 p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-2">Media &amp; press</h2>
            <p>
              Email <a href="mailto:hello@relaywork.co" className="text-indigo-600 font-semibold hover:text-indigo-500">hello@relaywork.co</a><br />
              Subject line: “Press Inquiry”
            </p>
          </div>
          <div className="bg-white rounded-2xl shadow border border-gray-100 p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-2">Mailing</h2>
            <p>Mule Works Inc. dba Relay Work<br />Houston, TX 77008</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
